import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/config';
import { Building2, CreditCard, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const Billing = () => {
    const { t } = useTranslation();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const { userData } = useAuth();

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const { data, error: fetchError } = await supabase.from('tenants').select('*');
                if (fetchError) throw fetchError;
                setTenants(data || []);
            } catch (error) {
                console.error("Error fetching tenants for billing:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchTenants();
    }, []);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'paid': return <CheckCircle2 size={16} style={{ color: '#10b981' }} />;
            case 'overdue': return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
            case 'pending': return <Clock size={16} style={{ color: '#f59e0b' }} />;
            default: return <Clock size={16} style={{ color: 'var(--text-muted)' }} />;
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'paid': return { background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' };
            case 'overdue': return { background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' };
            case 'pending': return { background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' };
            default: return { background: 'var(--bg-main)', color: 'var(--text-muted)' };
        }
    };

    if (!userData?.isSuperAdmin) {
        return <div className="p-8 text-center">{t('common.accessDenied')}</div>;
    }

    return (
        <div>
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem' }}>{t('billing.title')}</h1>
                <p style={{ color: 'var(--text-muted)' }}>{t('billing.subtitle')}</p>
            </div>

            <div className="card glass" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                                <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{t('billing.business')}</th>
                                <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{t('billing.plan')}</th>
                                <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{t('billing.status')}</th>
                                <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{t('billing.amount')}</th>
                                <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{t('billing.lastPayment')}</th>
                                <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{t('billing.nextPayment')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" style={{ padding: '3rem', textAlign: 'center' }}>{t('common.loading')}</td></tr>
                            ) : tenants.length === 0 ? (
                                <tr><td colSpan="6" style={{ padding: '3rem', textAlign: 'center' }}>No records found.</td></tr>
                            ) : tenants.map((tenant) => {
                                // Fallback mock data if subscription fields don't exist
                                const subscription = tenant.subscription || {
                                    plan: 'basic',
                                    status: 'paid',
                                    amount: 29.99,
                                    lastPayment: new Date(),
                                    nextPayment: new Date(new Date().setMonth(new Date().getMonth() + 1))
                                };

                                return (
                                    <tr key={tenant.id} style={{ borderBottom: '1px solid var(--border)' }} className="table-row-hover">
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div className="logo-icon" style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '8px',
                                                    background: '#6f4e3710',
                                                    color: '#6f4e37',
                                                    border: '1px solid var(--border)'
                                                }}>
                                                    <Building2 size={16} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{tenant.name}</div>
                                                    <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>ID: {tenant.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <span style={{ fontSize: '0.875rem', fontWeight: 500, textTransform: 'capitalize' }}>
                                                {t(`billing.plans.${subscription.plan}`)}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '4px 10px',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                ...getStatusStyle(subscription.status)
                                            }}>
                                                {getStatusIcon(subscription.status)}
                                                {t(`billing.statuses.${subscription.status}`)}
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', fontWeight: 700 }}>
                                            ${subscription.amount}
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                                            {subscription.lastPayment?.toDate ? subscription.lastPayment.toDate().toLocaleDateString() : new Date(subscription.lastPayment).toLocaleDateString()}
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                                            {subscription.nextPayment?.toDate ? subscription.nextPayment.toDate().toLocaleDateString() : new Date(subscription.nextPayment).toLocaleDateString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Billing;
